import {
  ComponentPropsWithoutRef,
  forwardRef,
  ForwardedRef,
  ChangeEvent,
  useState,
  ReactElement,
  KeyboardEvent
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareExclamation } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';
import { Text, Flexbox } from '@morressier/ts';
import { getFileExtension } from 'utils/utils';

const StyledLabel = styled.label`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 1.25rem;
  color: var(--color-mono-0);
  background-color: var(--color-mono-100);
  border: 1px solid var(--color-mono-60);

  cursor: pointer;
  &:hover,
  &:focus {
    background-color: var(--color-mono-95);
    border: 1px solid var(--color-mono-0);
  }
  font-size: var(--fontSizes-normal);
  line-height: var(--lineHeights-normal);
  svg {
    font-size: var(--fontSizes-m);
    padding-right: 0.5rem;
  }
  &:focus {
    outline: var(--focus-style);
    outline-offset: 2px;
  }
`;
export const Error = styled(Text).attrs({
  size: 'body1_new',
  textAs: 'span'
})`
  margin-top: 8px;
  color: var(--color-red-50);
  svg {
    margin-right: 4px;
  }
`;

interface IFileUploader extends ComponentPropsWithoutRef<'input'> {
  icon?: ReactElement | null;
}

const FileUploader = (
  props: IFileUploader,
  ref: ForwardedRef<HTMLInputElement>
) => {
  const [error, setError] = useState('');
  const { className, accept, onChange, icon, children, ...rest } = props;

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files?.[0];
    const MAX_SIZE_IN_KB = 10 * 1024;
    if (file) {
      const fileSizeInKB = file.size / 1024;
      const fileExtension = getFileExtension(file.name);
      const allowedFileExtensions = accept?.split(',');

      setError('');

      if (fileSizeInKB > MAX_SIZE_IN_KB) {
        setError('Please choose an image smaller than 10 MB.');
      } else if (!allowedFileExtensions?.includes(`.${fileExtension}`)) {
        setError('Please choose an image with .png, .jpg, or .jpeg formats.');
      } else if (onChange) {
        onChange(e);
      }
    }
  };

  const onKeyPres = (e: KeyboardEvent) => {
    e.preventDefault();
    if (e.key === 'Enter' && ref) {
      const inputRef = ref as any;
      inputRef?.current.click();
    }
  };

  return (
    <Flexbox gap="1rem">
      <StyledLabel className={className} tabIndex={0} onKeyPress={onKeyPres}>
        <input
          {...rest}
          type="file"
          ref={ref}
          hidden
          accept={accept}
          onChange={handleFileSelect}
        />
        {icon}
        {children}
      </StyledLabel>
      {error && (
        <Error>
          <FontAwesomeIcon icon={faSquareExclamation} /> {error}
        </Error>
      )}
    </Flexbox>
  );
};

export default forwardRef(FileUploader);
